import { ClaimRecord, ClaimRecordStatus } from '@typesApp/ClaimRecord'
import { InsuranceStatusErrorCode } from '@typesApp/ClaimData'
import { isProductInsuranceEligible } from './isProductInsuranceEligible'
import { isInsuranceUsageEnabled, isInsuranceUsageDisabled } from './insuranceMemberUtil'
import { ProductTypesEligibility } from '@typesApp/Product'

type ToggleState = {
  toggleOn: boolean
  allowOn: boolean
  allowSelectProduct?: boolean
}

type GetToggleStateParams = {
  claimRecord?: ClaimRecord
  claimRecordDeleted?: boolean
}

type GetToggleMessageKeyParams = GetToggleStateParams & {
  initError?: boolean
  productQuoteError?: boolean
}

type GetToggleEligibilityMessageKeyParams = GetToggleStateParams & {
  hasEligibleProduct?: boolean
}

type GetToggleMessageKeyForCartParams = GetToggleStateParams & {
  initError?: boolean
}

export const getInsuranceToggleState = (params: GetToggleStateParams): ToggleState => {
  const { claimRecord, claimRecordDeleted } = params
  const toggleOn = isToggleOn(claimRecord, claimRecordDeleted)
  const allowOn = isInsuranceUsageDisabled(claimRecord?.claimData?.useInsuranceStatus)

  return {
    toggleOn,
    allowOn,
    allowSelectProduct: false,
  }
}

const isToggleOn = (claimRecord: ClaimRecord | undefined, claimRecordDeleted?: boolean): boolean => {
  const insuranceUsage = claimRecord?.claimData?.useInsuranceStatus
  return !claimRecordDeleted && isInsuranceUsageEnabled(insuranceUsage)
}

export const getInsuranceToggleStateForCart = (params: GetToggleStateParams): ToggleState => {
  const { claimRecord, claimRecordDeleted } = params

  const hasBenefits = claimRecord?.status !== ClaimRecordStatus.NO_BENEFITS_AVAILABLE
  const countOfEligibleCartProducts = (claimRecord?.eligibleProducts ?? []).filter(isProductInsuranceEligible).length
  const toggleOn = isToggleOn(claimRecord, claimRecordDeleted)
  const allowOn = isInsuranceUsageDisabled(claimRecord?.claimData?.useInsuranceStatus)

  return {
    toggleOn,
    allowOn,
    allowSelectProduct: hasBenefits && countOfEligibleCartProducts > 1 && toggleOn,
  }
}

export const getInsuranceToggleMessageKey = (params: GetToggleMessageKeyParams): string | undefined => {
  const { claimRecord, claimRecordDeleted, initError, productQuoteError } = params

  if (!claimRecord || claimRecordDeleted) return

  if (
    claimRecord.status === ClaimRecordStatus.NO_BENEFITS_AVAILABLE ||
    claimRecord.claimData?.insuranceStatusErrorCode === InsuranceStatusErrorCode.BENEFITS_UNAVAILABLE
  ) {
    return InsuranceStatusErrorCode.BENEFITS_UNAVAILABLE
  }

  if (productQuoteError) {
    return InsuranceStatusErrorCode.PRODUCT_QUOTE_FAILED
  }

  if (initError) {
    return InsuranceStatusErrorCode.INIT_ERROR
  }
}

export const getInsuranceToggleEligibilityMessageKey = (
  params: GetToggleEligibilityMessageKeyParams
): string | undefined => {
  const { claimRecord, claimRecordDeleted, hasEligibleProduct } = params

  if (!claimRecord || claimRecordDeleted) return

  // Note: for non cart, if product is not eligible we show 'benefits not available' message
  if (!hasEligibleProduct) {
    return InsuranceStatusErrorCode.BENEFITS_UNAVAILABLE
  }
}

export const getInsuranceToggleMessageKeyForCart = (params: GetToggleMessageKeyForCartParams): string | undefined => {
  const { claimRecord, claimRecordDeleted, initError } = params
  if (claimRecordDeleted) return

  if (
    claimRecord?.claimData?.insuranceStatusErrorCode &&
    claimRecord?.claimData?.insuranceStatusErrorCode !== InsuranceStatusErrorCode.NONE
  ) {
    return claimRecord?.claimData?.insuranceStatusErrorCode
  }

  if (initError) {
    return InsuranceStatusErrorCode.INIT_ERROR_ON_CART
  }
}

export const hasEligibleProductType = (productTypesEligibility: ProductTypesEligibility): boolean => {
  return productTypesEligibility?.productTypesEligibility?.some(item => item.benefitAvailability === 'SUPPORTED')
}
